import { Pulse } from 'easyship-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';

import { useConfirmPaymentIntent } from '@/hooks/useConfirmPaymentIntent';
import { useCreateOrRetrievePaymentIntent } from '@/hooks/useCreateOrRetrievePaymentIntent';
import { useFailPaymentIntent } from '@/hooks/useFailPaymentIntent';
import { useOrderDetails } from '@/hooks/useOrderDetails';
import { ErrorPage } from '@/pages/ErrorPage';
import { Mixpanel } from '@/services/mixpanel';
import { InsurancePaymentIntentRequest } from '@/types/insurance';
import { getSessionId } from '@/utils/getSessionId';

import { PaymentIntentContext } from './context';

const BUYER_INSURANCE_TOKEN = 'buyer_insurance_token';
const EASYSHIP_SHIPMENT_ID = 'easyship_shipment_id';
const OFFER_ORIGIN = 'offer_origin';

const isValidOfferOrigin = (value: string | null): value is 'tracking_email' | 'tracking_page' =>
  value === 'tracking_email' || value === 'tracking_page';

export const PaymentIntentProvider = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get(BUYER_INSURANCE_TOKEN);
  const easyshipShipmentId = searchParams.get(EASYSHIP_SHIPMENT_ID);
  const offerOriginParam = searchParams.get(OFFER_ORIGIN);

  const offerOrigin: 'tracking_email' | 'tracking_page' | null = token
    ? 'tracking_email'
    : isValidOfferOrigin(offerOriginParam)
      ? offerOriginParam
      : null;

  const getPaymentIntentRequest = useCallback((): InsurancePaymentIntentRequest | null => {
    if (token) {
      return { buyer_insurance_token: token };
    }

    if (easyshipShipmentId && isValidOfferOrigin(offerOriginParam)) {
      return { easyship_shipment_id: easyshipShipmentId, offer_origin: offerOriginParam };
    }

    return null;
  }, [token, easyshipShipmentId, offerOriginParam]);

  const {
    data: orderDetailsData,
    mutate: fetchOrderDetails,
    isPending: isOrderDetailsPending,
    error: orderDetailsError,
  } = useOrderDetails();
  const {
    data: stripeIntentData,
    mutate: createOrRetrieve,
    isPending: isContinuingToPayment,
    error: continueToPaymentRawError,
  } = useCreateOrRetrievePaymentIntent();
  const { data: confirmData, mutate: confirm } = useConfirmPaymentIntent();
  const { mutate: fail } = useFailPaymentIntent();

  const [isThankYouShown, setIsThankYouShown] = useState(false);
  const hasTrackedVisitRef = useRef(false);

  useEffect(() => {
    const paymentIntentRequest = getPaymentIntentRequest();

    if (paymentIntentRequest) {
      fetchOrderDetails(paymentIntentRequest);
    }
  }, [fetchOrderDetails, getPaymentIntentRequest]);

  useEffect(() => {
    if (!hasTrackedVisitRef.current && orderDetailsData?.easyship_shipment_id) {
      hasTrackedVisitRef.current = true;
      Mixpanel.identify(orderDetailsData.easyship_shipment_id);
      Mixpanel.track('Payment Portal Visited', {
        shipment_id: orderDetailsData.easyship_shipment_id,
        session_id: getSessionId(),
        context: offerOrigin,
      });
    }
  }, [orderDetailsData?.easyship_shipment_id, offerOrigin]);

  const continueToPayment = useCallback(() => {
    const paymentIntentRequest = getPaymentIntentRequest();

    if (paymentIntentRequest) {
      createOrRetrieve(paymentIntentRequest);
    }
  }, [createOrRetrieve, getPaymentIntentRequest]);

  const confirmPaymentIntent = useCallback(() => {
    const paymentIntentRequest = getPaymentIntentRequest();

    if (paymentIntentRequest) {
      return new Promise<void>((resolve, reject) => {
        confirm(paymentIntentRequest, {
          onSuccess: () => {
            resolve();
            setIsThankYouShown(true);
          },
          onError: (error) => {
            reject(error);
          },
        });
      });
    }

    return Promise.reject();
  }, [confirm, getPaymentIntentRequest]);

  const failPaymentIntent = useCallback(() => {
    const paymentIntentRequest = getPaymentIntentRequest();

    if (paymentIntentRequest) {
      fail(paymentIntentRequest);
    }
  }, [fail, getPaymentIntentRequest]);

  if (!token && !easyshipShipmentId) {
    return <ErrorPage errorMessageId="errors.missing-shipment-id" />;
  }

  if (!token && !isValidOfferOrigin(offerOriginParam)) {
    return <ErrorPage errorMessageId="errors.invalid-offer-origin" />;
  }

  const orderDetailsErrorMessage =
    orderDetailsError?.response?.data?.error || orderDetailsError?.message;

  if (orderDetailsErrorMessage) {
    return <ErrorPage error={orderDetailsErrorMessage} />;
  }

  if (!orderDetailsData || isOrderDetailsPending) {
    return <Pulse />;
  }

  const data = {
    ...(confirmData ?? orderDetailsData),
    stripe_payment_intent: stripeIntentData?.stripe_payment_intent,
  };

  const continueToPaymentError =
    continueToPaymentRawError?.response?.data?.error || continueToPaymentRawError?.message;

  return (
    <PaymentIntentContext.Provider
      value={{
        data,
        offerOrigin: offerOrigin ?? undefined,
        confirmPaymentIntent,
        failPaymentIntent,
        continueToPayment,
        isContinuingToPayment,
        continueToPaymentError,
        isThankYouShown,
      }}
    >
      <Outlet />
    </PaymentIntentContext.Provider>
  );
};
